import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import Select from "react-select";
import { postCouponsAPI } from "../../MyApi/coupons";
import { getProducts } from "../../MyApi/product";
import { getCategories } from "../../MyApi/product/categories";
import { getStores } from "../../MyApi/stores";
import { getCountries } from "../../MyApi/admin/countries/countries";
import EnhancedSearchStore from "../../Components/UI/enhancedstoresearch";
import EnhancedSearchProduct from "../../Components/UI/enhancedproductsearch";


const VoucherModal = ({ isOpen, toggle, orderId, onSuccess, currencySymbol = "EGP" ,user_id ,refetchOrder }) => {
  const [formData, setFormData] = useState({
    code: "",
    discount_amount: "",
    selectedUser: "",
    discount_type: "",
    usage_limit: "1",
    usage_limit_per_user: "1",
    minimum_order_amount: "",
    start_date: "",
    end_date: "",
    is_active: 1,
    name: "",
    country_id: "",
    currency_factor: 1,
    selectedStores: [],
    selectedCategories: [],
    selectedProducts: [],
  });

  const [couponScope, setCouponScope] = useState("stores");
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);

  const { data: productsData } = useQuery("getProducts", () => getProducts());
  const { data: storesData } = useQuery("getStores", () => getStores());
  const { data: categoriesData } = useQuery("getCategories", () => getCategories());
  const { data: countriesData } = useQuery("getCountries", getCountries);

  const products = productsData?.data?.products || [];
  const stores = storesData?.data?.Stores || [];
  const categories = categoriesData?.data?.categories || [];
  const countries = countriesData?.data?.countries || [];

  const mutation = useMutation(
    (couponData) => postCouponsAPI(couponData),
    {
      onSuccess: (data) => {
        toast.success("تم إرسال القسيمة بنجاح");
        onSuccess();
        toggle();
        // Reset form
        resetForm();
        refetchOrder()
      },
      onError: (error) => {
        toast.error("فشل في إرسال القسيمة");
      }
    }
  );

  const resetForm = () => {
    setFormData({
      code: "",
      discount_amount: "",
      discount_type: "",
      usage_limit: "1",
      usage_limit_per_user: "1",
      minimum_order_amount: "",
      start_date: "",
      end_date: "",
      is_active: 1,
      name: "",
      country_id: "",
      currency_factor: 1,
      selectedStores: [],
      selectedCategories: [],
      selectedProducts: [],
    });
    setCouponScope("stores");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.code.trim()) {
      toast.error("يرجى إدخال كود القسيمة");
      return;
    }
    
    if (!formData.discount_amount || parseFloat(formData.discount_amount) <= 0) {
      toast.error("يرجى إدخال مبلغ القسيمة");
      return;
    }
    
    if (!formData.discount_type) {
      toast.error("يرجى اختيار نوع الخصم");
      return;
    }
    
    if (!formData.name.trim()) {
      toast.error("يرجى إدخال اسم القسيمة");
      return;
    }
    
    if (!formData.country_id) {
      toast.error("يرجى اختيار الدولة");
      return;
    }
    
    if (!formData.start_date) {
      toast.error("يرجى إدخال تاريخ البداية");
      return;
    }
    
    if (!formData.end_date) {
      toast.error("يرجى إدخال تاريخ النهاية");
      return;
    }

    // Create FormData to match coupon API structure
    const submitData = new FormData();
    submitData.append("code", formData.code);
    submitData.append("discount_amount", formData.discount_amount);
    submitData.append("discount_type", formData.discount_type);
    submitData.append("usage_limit", formData.usage_limit);
    submitData.append("usage_limit_per_user", formData.usage_limit_per_user);
    submitData.append("minimum_order_amount", formData.minimum_order_amount);
    submitData.append("start_date", formData.start_date);
    submitData.append("end_date", formData.end_date);
    submitData.append("is_active", formData.is_active);
    submitData.append("coupon_type", "regular");
    submitData.append("user_ids[]", user_id);
    submitData.append("object_type", "user");
    submitData.append("name", formData.name);
    submitData.append("country_id", formData.country_id);
    submitData.append("currency_factor", formData.currency_factor);

    // Add scope-specific IDs
    if (couponScope === "products") {
      formData.selectedProducts.forEach((product) =>
        submitData.append("product_ids[]", product.id),
      );
    } else if (couponScope === "stores") {
      formData.selectedStores.forEach((store) =>
        submitData.append("store_ids[]", store.id),
      );
    } else if (couponScope === "categories") {
      formData.selectedCategories.forEach((category) =>
        submitData.append("category_ids[]", category.value),
      );
    }

    mutation.mutate(submitData);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target.checked ? 1 : 0) : value
    }));
  };

  const handleSelectChange = (field, selectedOptions) => {
    setFormData(prev => ({
      ...prev,
      [field]: selectedOptions,
    }));
  };

  const setStoreId = (selectedOptions) => {
    setFormData(prev => ({
      ...prev,
      selectedStores: selectedOptions,
    }));
  };

  const setProductId = (selectedOptions) => {
    setFormData(prev => ({
      ...prev,
      selectedProducts: selectedOptions,
    }));
  };

  const handleChangeCountry = (selectedOption) => {
    setFormData(prev => ({ 
      ...prev, 
      country_id: selectedOption?.id, 
      currency_factor: selectedOption?.currency_factor 
    }));
  };

  const generateVoucherCode = () => {
    setIsGeneratingCode(true);
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setFormData(prev => ({
      ...prev,
      code
    }));
    setIsGeneratingCode(false);
  };

  const isFormValid = formData.code.trim() && 
                     formData.discount_amount && 
                     parseFloat(formData.discount_amount) > 0 && 
                     formData.discount_type &&
                     formData.name.trim() &&
                     formData.country_id &&
                     formData.start_date &&
                     formData.end_date;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>
        إرسال قسيمة تعويض
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="text-center mb-4">
            <div className="alert alert-info">
              <strong>ملاحظة:</strong> سيتم إرسال القسيمة للعميل كتعويض عن الخدمة
            </div>
          </div>
          
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label">الدولة</label>
              <Select
                placeholder="اختر الدولة"
                isLoading={countriesData === undefined}
                options={countries}
                getOptionLabel={(e) => e.name}
                getOptionValue={(e) => e.id}
                value={countries.find((country) => country.id === formData.country_id)}
                onChange={handleChangeCountry}
              />
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">اسم القسيمة</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="مثلاً: تعويض عن التأخير"
                required
              />
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">كود القسيمة</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="مثلاً: SORRY10"
                  required
                />
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={generateVoucherCode}
                  disabled={isGeneratingCode}
                >
                  {isGeneratingCode ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : (
                    "توليد كود"
                  )}
                </button>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">عدد القسائم المسموح باستخدامها</label>
              <input
                type="number"
                className="form-control"
                name="usage_limit"
                value={formData.usage_limit}
                onChange={handleChange}
                min="1"
                required
              />
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">عدد القسائم المسموح لكل مستخدم</label>
              <input
                type="number"
                className="form-control"
                name="usage_limit_per_user"
                value={formData.usage_limit_per_user}
                onChange={handleChange}
                min="1"
                required
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label mb-3">تطبيق القسيمة على:</label>
              <div className="d-flex flex-column gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="couponScope"
                    id="scopeProducts"
                    value="products"
                    checked={couponScope === "products"}
                    onChange={(e) => {
                      setCouponScope(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        selectedStores: [],
                        selectedCategories: [],
                      }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="scopeProducts">
                    المنتجات
                  </label>
                </div>

                {couponScope === "products" && (
                  <EnhancedSearchProduct  
                    isMulti={true} 
                    value={formData.selectedProducts} 
                    setProductId={setProductId} 
                  />
                )}

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="couponScope"
                    id="scopeStores"
                    value="stores"
                    checked={couponScope === "stores"}
                    onChange={(e) => {
                      setCouponScope(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        selectedProducts: [],
                        selectedCategories: [],
                      }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="scopeStores">
                    المتاجر
                  </label>
                </div>

                {couponScope === "stores" && (
                  <EnhancedSearchStore  
                    isMulti={true} 
                    value={formData.selectedStores} 
                    setStoreId={setStoreId}
                  />    
                )}

             
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="couponScope"
                    id="scopeCategories"
                    value="categories"
                    checked={couponScope === "categories"}
                    onChange={(e) => {
                      setCouponScope(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        selectedProducts: [],
                        selectedStores: [],
                      }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="scopeCategories">
                    الفئات
                  </label>
                </div>

                {couponScope === "categories" && (
                  <Select
                    isMulti
                    options={categories.map((cat) => ({
                      value: cat.id,
                      label: cat.name,
                    }))}
                    value={formData.selectedCategories}
                    onChange={(selected) =>
                      handleSelectChange("selectedCategories", selected)
                    }
                    placeholder="اختر الفئات"
                  />
                )}
              </div>
            </div>

            <div className="col-4 mb-3">
              <label className="form-label">نوع الخصم</label>
              <select
                className="form-select"
                name="discount_type"
                value={formData.discount_type}
                onChange={handleChange}
                required
              >
                <option value="">اختر نوع الخصم</option>
                <option value="percentage">نسبة</option>
                <option value="fixed">ثابت</option>
              </select>
            </div>
            
            <div className="col-8 mb-3 d-flex align-items-end">
              <input
                type="number"
                className="form-control"
                name="discount_amount"
                value={formData.discount_amount}
                onChange={handleChange}
                placeholder="ادخل قيمة الخصم"
                min="0"
                step="0.01"
                required
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">الحد الأدنى للطلب</label>
              <input
                type="number"
                className="form-control"
                name="minimum_order_amount"
                value={formData.minimum_order_amount}
                onChange={handleChange}
                min="0"
                step="0.01"
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">تاريخ البداية</label>
              <input
                type="date"
                className="form-control"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">تاريخ النهاية</label>
              <input
                type="date"
                className="form-control"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-12 mb-3">
              <div>
                <label htmlFor="">نشط</label>
                <label className="switch">
                  <input
                    type="checkbox"
                    name="is_active"
                    onChange={handleChange}
                    checked={formData.is_active === 1}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="d-flex justify-content-between w-100">
            <button 
              type="button" 
              className="btn btn-white" 
              onClick={toggle}
              disabled={mutation.isLoading}
            >
              إلغاء
            </button>
            <button 
              type="submit" 
              className="btn btn-success"
              disabled={mutation.isLoading || !isFormValid}
            >
              {mutation.isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  جاري المعالجة...
                </>
              ) : (
                "إرسال القسيمة"
              )}
            </button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default VoucherModal;
